import React, { useCallback, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Grid,
  Box,
  makeStyles,
  useTheme
} from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'

import Text from '../../atoms/Text'
import RoundedButton from '../../atoms/RoundedButton'
import Dialog from '../../molecules/Dialog/Dialog'
import Select from '../../molecules/Select'
import SnackAlert from '../../molecules/SnackAlert/SnackAlert'
import TextInput from '../../molecules/TextInput'

import { useBoolean, useGetInputProps } from '../../../hooks'

import { getSecurityQuestions, setRecoveryQuestion } from '../../../service'
import { messageToUse } from '../../pages/admin/common'
import { generatePassErrorMessage } from '../../../utils'

import {
  BUTTON_SIZES,
  SUCCESS_ALERT,
  ERROR_ALERT,
  API_ERROR_CODES
} from '../../../constants'

const useStyles = makeStyles((theme) => ({
  resetQnDialog: {
  },
  resetQnHeader: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '0',
      paddingTop: '0',
      '& > h2 > p': {
        fontSize: '1.125rem !important'
      }
    }
  },
  resetQnBody: {
    padding: '0 2.75rem',
    marginTop: '3.25rem',
    [theme.breakpoints.down('sm')]: {
      padding: '0.5rem 1.5rem'
    }
  },
  modalActions: {
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      '& > div': {
        flexDirection: 'column-reverse'
      }
    }
  },
  confirmError: {
    textAlign: 'left',
    marginTop: '5px'
  },
  selectQuestion: {
    width: '100% !important',
    padding: '16px 14px',
    border: '2px solid #EEF0F8',
    opacity: '1'
  }
}))

const ResetRecoveryQnDialog = ({
  username,
  openedDialog,
  closeDialog,
  updateQuestion
}) => {
  const classes = useStyles()
  const theme = useTheme()

  const [questions, setQuestions] = useState([])
  const [question, setQuestion] = useState(null)
  const [alert, setAlert] = useState({})
  const [isSaving, setIsSaving] = useBoolean(false)

  const [showPasswords, getInputProps] = useGetInputProps({
    password: false
  }, false)

  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { isSubmitting, isValid, errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: { question: '', answer: '', password: '' }
  })

  const onSubmitHandler = useCallback(async (values) => {
    try {
      setIsSaving.on()

      const body = {
        email: encodeURIComponent(username),
        securityQuestion: values.question,
        securityAnswer: values.answer,
        password: values.password
      }
      await setRecoveryQuestion(body)

      closeDialog()
      reset()
      updateQuestion(true)

      setAlert({ ...SUCCESS_ALERT })
    } catch (err) {
      if (err.code === API_ERROR_CODES.FORBIDDEN || err.code === API_ERROR_CODES.BAD_REQUEST) {
        ERROR_ALERT.alertMessage = generatePassErrorMessage(err)
      }

      setAlert({ ...ERROR_ALERT })
    } finally {
      setIsSaving.off()
    }
  }, [
    closeDialog,
    reset,
    username,
    setIsSaving,
    updateQuestion
  ])

  const loadSecurityQuestions = useCallback(async () => {
    try {
      const { data: questions } = await getSecurityQuestions({
        email: encodeURIComponent(username)
      })

      const securityQuestions = questions.map((item) => ({
        value: item.question,
        label: item.questionText
      }))

      setQuestions(securityQuestions)
    } catch (error) {
      console.error(error)
    }
  }, [username])

  useEffect(() => {
    loadSecurityQuestions()
  }, [loadSecurityQuestions])

  return (
    <>
      <Dialog size='md' open={openedDialog} onClose={closeDialog} className={classes.resetQnDialog}>
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          <Dialog.Header className={classes.resetQnHeader}>
            <Text
              text='Change Security Question'
              color={theme.palette.summitBlue}
              customFontSize='1.5rem'
              customFontWeight='900'
            />
          </Dialog.Header>
          <Dialog.Body className={classes.resetQnBody}>
            <Grid container>
              <Grid item xs={12}>
                <Box marginBottom='1.125rem'>
                  <Controller
                    name='question'
                    render={({ field: { onChange, onBlur } }) => (
                      <Select
                        placeholder='Security Question'
                        variant='outlined'
                        size='medium'
                        defaultValue=''
                        className={classes.selectQuestion}
                        options={questions}
                        onBlur={onBlur}
                        onChange={(event) => {
                          const option = questions.find(
                            (question) => question.value === event
                          )
                          onChange(event)
                          setQuestion(option)
                        }}
                        value={question?.value}
                        preventOptionsOverflowContainer
                        customOpts={{
                          whiteHover: true,
                          textCenter: true
                        }}
                      />)}
                    control={control}
                    rules={{ required: true }}
                  />
                  {errors.question && (
                    <Text
                      text={messageToUse(errors.question)}
                      color={theme.palette.error.main}
                      className={classes.confirmError}
                    />
                  )}
                </Box>
                <Box marginBottom='1.125rem'>
                  <Controller
                    name='answer'
                    render={() => (
                      <TextInput
                        type='text'
                        name='answer'
                        placeholder='Answer*'
                        autoComplete='no'
                        fullWidth
                        {...register('answer')}
                      />)}
                    control={control}
                    rules={{ required: true }}
                  />
                  {errors.answer && (
                    <Text
                      text={messageToUse(errors.answer)}
                      color={theme.palette.error.main}
                      className={classes.confirmError}
                    />
                  )}
                </Box>
                <Box marginBottom='3.375rem'>
                  <Controller
                    name='password'
                    render={() => (
                      <TextInput
                        type={showPasswords.password ? 'text' : 'password'}
                        inputProps={getInputProps('password')}
                        name='password'
                        placeholder='Password*'
                        autoComplete='new-password'
                        fullWidth
                        {...register('password')}
                      />)}
                    control={control}
                    rules={{ required: true }}
                  />
                  {errors.password && (
                    <Text
                      text={messageToUse(errors.password)}
                      color={theme.palette.error.main}
                      className={classes.confirmError}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Dialog.Body>
          <Dialog.Actions className={classes.modalActions}>
            <Grid container spacing={4} justifyContent='center'>
              <Grid item lg={4} xs={12}>
                <RoundedButton
                  fullWidth
                  onClick={closeDialog}
                  size={BUTTON_SIZES.large}
                >
                  Cancel
                </RoundedButton>
              </Grid>
              <Grid item lg={4} xs={12}>
                <RoundedButton
                  primary
                  fullWidth
                  type='submit'
                  size={BUTTON_SIZES.large}
                  isLoading={isSaving}
                  disabled={!isValid || isSubmitting}
                >
                  Save
                </RoundedButton>
              </Grid>
            </Grid>
          </Dialog.Actions>
        </form>
      </Dialog>
      <SnackAlert alert={alert} />
    </>
  )
}

ResetRecoveryQnDialog.propTypes = {
  username: PropTypes.string.isRequired,
  openedDialog: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func,
  updateQuestion: PropTypes.func
}

export default ResetRecoveryQnDialog
